/**
 * Error Handling Utilities
 *
 * Provides utility functions for parsing, classifying, and handling errors
 * throughout the application.
 */

import { AxiosError } from 'axios';
import toast from 'react-hot-toast';

/**
 * API Error Response Structure
 */
interface ApiErrorResponse {
  detail?: string | { msg: string; type?: string }[] | { error_code: string; message: string };
  message?: string;
  error?: string;
}

/**
 * Parse API error and extract meaningful message
 */
export function parseApiError(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    // Check for response data
    if (axiosError.response?.data) {
      const data = axiosError.response.data;

      // Handle FastAPI validation errors (array of errors)
      if (Array.isArray(data.detail)) {
        const messages = data.detail.map(err => err.msg || 'Validation error');
        return messages.join(', ');
      }

      // Handle structured error objects: { error_code, message }
      if (typeof data.detail === 'object' && data.detail !== null && 'message' in data.detail) {
        return (data.detail as { message: string }).message;
      }

      // Handle simple string detail
      if (typeof data.detail === 'string') {
        return data.detail;
      }

      // Handle message field
      if (data.message) {
        return data.message;
      }

      // Handle error field
      if (data.error) {
        return data.error;
      }
    }

    // Handle specific status codes
    if (axiosError.response?.status) {
      return getStatusCodeMessage(axiosError.response.status);
    }

    // Handle network errors
    if (axiosError.message === 'Network Error' || !axiosError.response) {
      return 'Network error. Please check your internet connection.';
    }

    // Handle timeout
    if (axiosError.code === 'ECONNABORTED') {
      return 'Request timeout. Please try again.';
    }

    // Return axios error message as fallback
    return axiosError.message || 'An error occurred while making the request';
  }

  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Last resort
  return 'An unexpected error occurred';
}

/**
 * Get user-friendly message for HTTP status codes
 */
function getStatusCodeMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This operation conflicts with existing data.';
    case 422:
      return 'Invalid data provided. Please check your input.';
    case 429:
      return 'Too many requests. Please slow down and try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again.';
    case 503:
      return 'Service unavailable. Please try again later.';
    default:
      if (status >= 500) {
        return 'Server error. Please try again later.';
      }
      if (status >= 400) {
        return 'Client error. Please check your request.';
      }
      return `Unexpected error (${status})`;
  }
}

/**
 * Show error notification to user using toast
 */
export function showErrorNotification(error: unknown, customMessage?: string): void {
  const message = customMessage || parseApiError(error);

  toast.error(message, {
    duration: 5000,
    position: 'top-right',
  });
}

/**
 * Show success notification to user
 */
export function showSuccessNotification(message: string): void {
  toast.success(message, {
    duration: 3000,
    position: 'top-right',
  });
}

/**
 * Show warning notification to user
 */
export function showWarningNotification(message: string): void {
  toast(message, {
    duration: 4000,
    position: 'top-right',
    icon: '⚠️',
  });
}

/**
 * Show info notification to user
 */
export function showInfoNotification(message: string): void {
  toast(message, {
    duration: 3000,
    position: 'top-right',
    icon: 'ℹ️',
  });
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return (
      axiosError.message === 'Network Error' ||
      !axiosError.response ||
      axiosError.code === 'ECONNABORTED' ||
      axiosError.code === 'ERR_NETWORK'
    );
  }

  if (error instanceof Error) {
    return (
      error.message.toLowerCase().includes('network') ||
      error.message.toLowerCase().includes('connection')
    );
  }

  return false;
}

/**
 * Check if error is an authentication error (401/403)
 */
export function isAuthError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 401 || axiosError.response?.status === 403;
  }

  return false;
}

/**
 * Check if error is a rate limit error (429)
 */
export function isRateLimitError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 429;
  }

  return false;
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;
    return status !== undefined && status >= 500 && status < 600;
  }

  return false;
}

/**
 * Determine if an operation should be retried based on the error
 */
export function shouldRetry(error: unknown, retryCount: number = 0, maxRetries: number = 3): boolean {
  // Don't retry if we've exceeded max retries
  if (retryCount >= maxRetries) {
    return false;
  }

  // Retry network errors
  if (isNetworkError(error)) {
    return true;
  }

  // Retry server errors (5xx)
  if (isServerError(error)) {
    return true;
  }

  // Retry rate limit errors (429) with backoff
  if (isRateLimitError(error)) {
    return true;
  }

  // Don't retry auth errors
  if (isAuthError(error)) {
    return false;
  }

  // Don't retry client errors (4xx) except specific cases
  if (isAxiosError(error)) {
    const status = (error as AxiosError).response?.status;
    if (status && status >= 400 && status < 500) {
      return false;
    }
  }

  return false;
}

/**
 * Get retry delay with exponential backoff
 */
export function getRetryDelay(retryCount: number): number {
  // Exponential backoff: 1s, 2s, 4s, 8s, etc.
  return Math.min(1000 * Math.pow(2, retryCount), 10000);
}

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}

/**
 * Retry an async operation with exponential backoff
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    onRetry?: (error: unknown, attempt: number) => void;
    shouldRetryFn?: (error: unknown, attempt: number) => boolean;
  } = {}
): Promise<T> {
  const { maxRetries = 3, onRetry, shouldRetryFn = shouldRetry } = options;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt < maxRetries && shouldRetryFn(error, attempt)) {
        // Calculate delay
        const delay = getRetryDelay(attempt);

        // Call retry callback
        if (onRetry) {
          onRetry(error, attempt + 1);
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // Don't retry, throw the error
        throw error;
      }
    }
  }

  // If we get here, all retries failed
  throw lastError;
}

/**
 * Log error to console with context
 */
export function logError(error: unknown, context?: string): void {
  const message = context ? `Error in ${context}:` : 'Error:';

  console.error(message, error);

  // In production, you could send to an error reporting service here
  // e.g., Sentry, LogRocket, etc.
}
